import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from '~/data/http/axios'
import AiLoadingIcon from '~/Common-Objects/LoadingIcons/aiLoadingIcon'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import ButtonSimple from '~/Common-Objects/UI/Forms/ButtonSimple/ButtonSimple'
import Icons from '~/Common-Objects/UI/Icons/index'
import NewUserForm from '~/Common-Objects/NewUserForm/newUserForm.jsx'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import StickyNavBottom from '~/Common-Objects/Navigation/StickNavBottom'
import TextArea from '~/Common-Objects/UI/Forms/TextArea/TextArea'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import WarningHolder from '~/Common-Objects/UI/Cards/WarningHolder'
import i18n from '~/i18n'
import helpers from '~/Classes/helpers'
import * as siteMaster from '~/data/actions/siteActions'
import '~/pages/cp/includes/aiCreateWizard/style.scss'

const SUGGESTION_KEYS = ['suggestion1', 'suggestion2', 'suggestion3']

const MODEL_OPTIONS = [
    { label: 'Gemini 3.8 Flash', value: 'gemini-3.8-flash' },
    { label: 'GPT-6.1 Sol', value: 'gpt-6.1-sol' }
]

const DEFAULT_MODEL = MODEL_OPTIONS[0].value
const ns = 'form:add.aiGenerate'

const AiFormGenerate = ({ history, groupId, initialFormType = 0, onBack, onClose }) => {
    const dispatch = useDispatch()
    const accountMaster = useSelector((state) => state.accountMaster)
    const [prompt, setPrompt] = useState('')
    const [formName, setFormName] = useState('')
    const [formType, setFormType] = useState([0, 1, 2].includes(Number(initialFormType)) ? Number(initialFormType) : 0)
    const [model, setModel] = useState(DEFAULT_MODEL)
    const [useAccountBranding, setUseAccountBranding] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [error, setError] = useState('')
    const [pendingDecisions, setPendingDecisions] = useState(null)
    const [decisionMessage, setDecisionMessage] = useState('')
    const [selectedChoices, setSelectedChoices] = useState({})

    const closeDrawer = () => {
        if (onClose) {
            onClose()
            return
        }
        dispatch(siteMaster.alterSiteDrawer(false, true, 'right', null, true, null))
    }

    const applySuggestion = (key) => {
        setPrompt(i18n.t(`${ns}.${key}`))
        setError('')
    }

    const openCreatedForm = async (formId, responseGroupId) => {
        const editorVersion = await helpers.getTemplateEditorVersion(accountMaster, true)
        const canUseV2 = !!editorVersion && editorVersion.canUseV2 === true
        const editorUrl = canUseV2 ? '/cp/templates/add/dragDropv2/' : '/cp/templates/add/dragDrop/'
        const redirect = `/cp/groups/${responseGroupId}/forms/create/${formId}/2`

        closeDrawer()
        history.push(
            `${editorUrl}?form=true&groupId=${responseGroupId}&formId=${formId}&redirect=${encodeURIComponent(redirect)}`
        )
    }

    const extractErrorMessage = (err) => {
        let message = i18n.t(`${ns}.genericError`)
        if (err?.data?.error) {
            message = err.data.error
        } else if (err?.response?.data?.error) {
            message = err.response.data.error
        } else if (err?.message && err.message !== 'Network Error') {
            message = err.message
        }
        return message
    }

    const buildRequestBody = (extra = {}) => {
        const resolvedGroupId = Number(groupId)
        const body = {
            prompt: (prompt || '').trim(),
            groupId: resolvedGroupId,
            formType,
            model,
            useAccountBranding,
            ...extra
        }
        if (formName.trim()) {
            body.formName = formName.trim()
        }
        return body
    }

    const handleGenerateResponse = async (response, resolvedGroupId) => {
        if (response.data?.status === 'needs_choice') {
            const decisions = Array.isArray(response.data.decisions) ? response.data.decisions : []
            const defaults = {}
            decisions.forEach((decision) => {
                const createChoice = (decision.choices || []).find((choice) => choice.action === 'create')
                defaults[decision.id] = createChoice?.id || decision.choices?.[0]?.id || 'skip'
            })
            setPendingDecisions(decisions)
            setDecisionMessage(response.data.message || i18n.t(`${ns}.decisionsSubtitle`))
            setSelectedChoices(defaults)
            setGenerating(false)
            return
        }

        const formId = response.data?.formId
        const responseGroupId = response.data?.groupId || resolvedGroupId
        if (!formId) {
            throw new Error(i18n.t(`${ns}.genericError`))
        }
        await openCreatedForm(formId, responseGroupId)
    }

    const generate = async () => {
        const trimmedPrompt = (prompt || '').trim()
        if (!trimmedPrompt) {
            setError(i18n.t(`${ns}.promptRequired`))
            return
        }

        const resolvedGroupId = Number(groupId)
        if (!Number.isInteger(resolvedGroupId) || resolvedGroupId <= 0) {
            setError(i18n.t(`${ns}.groupRequired`))
            return
        }

        if (!accountMaster?.accountMaster?.approved) {
            dispatch(
                siteMaster.alterFullpage(
                    <NewUserForm
                        from='campaign'
                        finish={() => {
                            dispatch(siteMaster.alterFullpage(undefined))
                        }}
                    />
                )
            )
            return
        }

        setGenerating(true)
        setError('')
        setPendingDecisions(null)
        setDecisionMessage('')
        setSelectedChoices({})

        try {
            const response = await axios.post('/ai/form/generate', buildRequestBody(), {
                timeout: 120000
            })
            await handleGenerateResponse(response, resolvedGroupId)
        } catch (err) {
            setError(extractErrorMessage(err))
            setGenerating(false)
        }
    }

    const continueWithDecisions = async () => {
        if (!pendingDecisions?.length) return

        const resolvedGroupId = Number(groupId)
        const fieldDecisions = pendingDecisions.map((decision) => ({
            decisionId: decision.id,
            choiceId: selectedChoices[decision.id] || 'skip'
        }))

        setGenerating(true)
        setError('')

        try {
            const response = await axios.post(
                '/ai/form/generate',
                buildRequestBody({
                    pendingDecisions,
                    fieldDecisions
                }),
                {
                    timeout: 120000
                }
            )
            await handleGenerateResponse(response, resolvedGroupId)
        } catch (err) {
            setError(extractErrorMessage(err))
            setGenerating(false)
        }
    }

    const decisionChoiceOptions = (decision) =>
        (decision.choices || []).map((choice) => ({
            label: choice.label,
            value: choice.id
        }))

    const stickyNav = (continueProps) => (
        <StickyNavBottom>
            <div className='quickFlex' style={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                {onBack && !generating ? (
                    <Button
                        iconLeft={<Icons icon='arrowLeft' />}
                        onClick={() => {
                            if (pendingDecisions?.length) {
                                setPendingDecisions(null)
                                setDecisionMessage('')
                                setSelectedChoices({})
                                setError('')
                                return
                            }
                            onBack()
                        }}
                    >
                        {i18n.t(`${ns}.back`)}
                    </Button>
                ) : (
                    <div />
                )}
                {continueProps && !generating ? (
                    <Button
                        className='button-primary mar-l5'
                        iconRight={<Icons icon='arrowRight' />}
                        disabled={continueProps.disabled}
                        onClick={continueProps.onClick}
                    >
                        {continueProps.label}
                    </Button>
                ) : null}
            </div>
        </StickyNavBottom>
    )

    return (
        <div className='ai-create-wizard ai-create-wizard--form' data-test='ai-form-generate'>
            <div className='ai-create-wizard__header'>
                <div className='ai-form-interview__beta'>
                    <SquareChip
                        className='square-chip-purple'
                        value={i18n.t('templates:aiContentAssistant.beta')}
                    />
                </div>
                <h2 className='ai-create-wizard__title'>
                    {i18n.t(pendingDecisions?.length ? `${ns}.decisionsTitle` : `${ns}.title`)}
                </h2>
                <p className='ai-create-wizard__subtitle'>
                    {pendingDecisions?.length ? i18n.t(`${ns}.decisionsSubtitle`) : i18n.t(`${ns}.subtitle`)}
                </p>
            </div>

            {generating ? (
                <div className='ai-create-wizard__loading' data-test='ai-form-generating'>
                    <AiLoadingIcon style={{ height: '160px', width: '100%' }} />
                    <p className='mar-t20 text-center'>
                        {pendingDecisions?.length ? i18n.t(`${ns}.continuing`) : i18n.t(`${ns}.generating`)}
                    </p>
                    <p className='text-center'>{i18n.t(`${ns}.generatingHint`)}</p>
                </div>
            ) : pendingDecisions?.length ? (
                <div className='ai-form-interview'>
                    <p className='mar-b20'>{decisionMessage}</p>
                    {pendingDecisions.map((decision) => (
                        <div key={decision.id} className='mar-b25'>
                            <div className='text-heavy mar-b5'>{decision.label}</div>
                            {decision.reason ? <p className='mar-b10'>{decision.reason}</p> : null}
                            <Select
                                name={`decision-${decision.id}`}
                                label={i18n.t(`${ns}.decisionChoiceLabel`)}
                                data={decisionChoiceOptions(decision)}
                                value={selectedChoices[decision.id]}
                                onChange={(ev) =>
                                    setSelectedChoices((prev) => ({
                                        ...prev,
                                        [decision.id]: ev.target.value
                                    }))
                                }
                            />
                        </div>
                    ))}
                    {error ? <WarningHolder className='mar-b15'>{error}</WarningHolder> : null}
                    {stickyNav({
                        label: i18n.t(`${ns}.continueButton`),
                        onClick: continueWithDecisions
                    })}
                </div>
            ) : (
                <div className='ai-form-interview'>
                    <div className='ai-form-interview__row'>
                        <TextInput
                            name='formName'
                            label={i18n.t(`${ns}.nameLabel`)}
                            placeholder={i18n.t(`${ns}.namePlaceholder`)}
                            value={formName}
                            onChange={(ev) => setFormName(ev.target.value)}
                        />
                        <div>
                            <div className='txtInput_label'>{i18n.t(`${ns}.formTypeLabel`)}</div>
                            <div className='ai-form-interview__types'>
                                <ButtonSimple
                                    className='button-simple-fullsize'
                                    selected={formType === 0}
                                    onClick={() => setFormType(0)}
                                >
                                    {i18n.t('form:index.hosted')}
                                </ButtonSimple>
                                <ButtonSimple
                                    className='button-simple-fullsize'
                                    selected={formType === 1}
                                    onClick={() => setFormType(1)}
                                >
                                    {i18n.t('form:index.embed')}
                                </ButtonSimple>
                            </div>
                        </div>
                    </div>
                    <TextArea
                        name='prompt'
                        label={i18n.t(`${ns}.promptLabel`)}
                        placeholder={i18n.t(`${ns}.promptPlaceholder`)}
                        value={prompt}
                        onChange={(ev) => {
                            setPrompt(ev.target.value)
                            setError('')
                        }}
                        rows={7}
                        error={error}
                    />
                    <div className='ai-create-wizard__suggestions'>
                        <div className='ai-create-wizard__suggestions-label'>{i18n.t(`${ns}.tryOneOfThese`)}</div>
                        {SUGGESTION_KEYS.map((key) => {
                            const text = i18n.t(`${ns}.${key}`)
                            return (
                                <button
                                    key={key}
                                    type='button'
                                    className={
                                        prompt === text
                                            ? 'ai-create-wizard__suggestion ai-create-wizard__suggestion--selected'
                                            : 'ai-create-wizard__suggestion'
                                    }
                                    onClick={() => applySuggestion(key)}
                                >
                                    {text}
                                </button>
                            )
                        })}
                    </div>
                    {/* <div className='ai-form-interview__branding'>
                        <Toggle
                            name='useAccountBranding'
                            checked={useAccountBranding}
                            onChange={() => setUseAccountBranding(!useAccountBranding)}
                            after={i18n.t(`${ns}.useAccountBranding`)}
                        />
                    </div> */}
                    <details className='ai-form-interview__advanced'>
                        <summary>{i18n.t(`${ns}.advanced`)}</summary>
                        <Select
                            name='model'
                            label={i18n.t(`${ns}.modelLabel`)}
                            data={MODEL_OPTIONS}
                            value={model}
                            onChange={(ev) => setModel(ev.target.value)}
                        />
                    </details>
                    {stickyNav({
                        label: i18n.t(`${ns}.generateButton`),
                        disabled: !prompt.trim(),
                        onClick: generate
                    })}
                </div>
            )}
        </div>
    )
}

export default AiFormGenerate
